import { useMutation } from "@tanstack/react-query";
import { UseFormReset } from "react-hook-form";
import { useLocation, useNavigate } from "react-router-dom";
import { z } from "zod";

import { ERROR_CODE, signup } from "~/api";
import { ROUTES } from "~/router";
import { signupSchema } from "~/screens/Auth/schemas/signup.schema";
import { useUserStore } from "~/stores";
import { validateError } from "~/utils";

export const useSignup = (
  formReset: UseFormReset<z.infer<typeof signupSchema>>,
) => {
  const navigate = useNavigate();
  const location = useLocation();
  const setHashedUserId = useUserStore((state) => state.setHashedUserId);
  return useMutation({
    mutationFn: signup,
    onSuccess: (data) => {
      const user = data.data;

      formReset();

      if (user.mustCompleteIntakeForm) {
        navigate(ROUTES.intakeForm, {
          state: {
            user,
          },
        });
      } else {
        navigate(ROUTES.emailSent, {
          state: { from: location, email: user.email },
        });
      }

      setHashedUserId(user.id);
    },
    onError: (error: unknown, variables) => {
      const parsedError = validateError(error);

      if (!parsedError) {
        return;
      }

      const { code, hash, user } = parsedError.response.data.error;

      switch (code) {
        case ERROR_CODE.pendingEmailVerification:
          if (hash) {
            setHashedUserId(hash);
            navigate(ROUTES.pendingVerification, {
              state: { from: location, email: variables.signupRequest.email },
            });
          }
          return;
        case ERROR_CODE.pendingIntakeForm:
          if (user) {
            setHashedUserId(user.hash);
            navigate(ROUTES.intakeForm, {
              state: {
                user,
              },
            });
          }
          return;
        default:
          return;
      }
    },
  });
};
